import type { PermissionAction, PermissionResource } from '@erp/shared';
import type { LucideIcon } from 'lucide-react';

export interface NavItem {
  to: string;
  icon: LucideIcon;
  labelKey: string;
  shortcut?: string;
  access?: { resource: PermissionResource; action: PermissionAction };
  accessAny?: readonly { resource: PermissionResource; action: PermissionAction }[];
  allowedRoles?: readonly string[];
  children?: readonly NavItem[];
}

export function canAccessNavigationItem(
  item: NavItem,
  canAccess: (resource: PermissionResource, action: PermissionAction) => boolean,
): boolean {
  return (
    (item.access === undefined || canAccess(item.access.resource, item.access.action)) &&
    (item.accessAny === undefined ||
      item.accessAny.some((entry) => canAccess(entry.resource, entry.action)))
  );
}

export function pathMatches(pathname: string, target: string): boolean {
  return pathname === target || pathname.startsWith(`${target}/`);
}

export function resolveNavigationTarget(
  item: NavItem,
  canAccess: (resource: PermissionResource, action: PermissionAction) => boolean,
  isRouteEnabled: (path: string) => boolean = () => true,
  roleKey?: string,
): string | null {
  if (item.allowedRoles !== undefined && !item.allowedRoles.some((role) => role === roleKey))
    return null;
  const canOpen = (entry: NavItem): boolean =>
    (entry.allowedRoles === undefined || entry.allowedRoles.some((role) => role === roleKey)) &&
    isRouteEnabled(entry.to) &&
    canAccessNavigationItem(entry, canAccess);
  if (canOpen(item)) return item.to;
  return item.children?.find(canOpen)?.to ?? null;
}

export function filterNavigation(
  items: readonly NavItem[],
  canAccess: (resource: PermissionResource, action: PermissionAction) => boolean,
  isRouteEnabled: (path: string) => boolean = () => true,
  roleKey?: string,
): readonly NavItem[] {
  return items.flatMap((item) => {
    if (resolveNavigationTarget(item, canAccess, isRouteEnabled, roleKey) === null) return [];
    if (item.children === undefined) return [item];
    return [
      {
        ...item,
        children: item.children.filter(
          (child) =>
            (child.allowedRoles === undefined ||
              child.allowedRoles.some((role) => role === roleKey)) &&
            isRouteEnabled(child.to) &&
            canAccessNavigationItem(child, canAccess),
        ),
      },
    ];
  });
}
